import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    WorkOrderItem,
    WorkOrderCreatePayload,
    WorkOrderUpdatePayload,
} from "../../interfaces/WorkOrder";
import { WorkOrdersResponse } from "./types";
import * as workOrderService from "../../services/workOrderService";

interface WorkOrderState {
    items: WorkOrderItem[];
    currentItem: WorkOrderItem | null;
    loading: boolean;
    error: string | null;
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
}

const initialState: WorkOrderState = {
    items: [], // Start with empty array - no mock data
    currentItem: null,
    loading: false,
    error: null,
    pageNumber: 1,
    pageSize: 200,
    totalCount: 0,
    totalPages: 0,
};

// Async thunk to fetch work orders from API
export const fetchWorkOrders = createAsyncThunk(
    "workOrders/fetchWorkOrders",
    async ({
        pageNumber = 1,
        pageSize = 200,
    }: {
        pageNumber?: number;
        pageSize?: number;
    } = {}) => {
        const response: WorkOrdersResponse = await workOrderService.getWorkOrders(
            pageNumber,
            pageSize
        );
        return response;
    }
);

// Async thunk to fetch work order by ID
export const fetchWorkOrderById = createAsyncThunk(
    "workOrders/fetchWorkOrderById",
    async (id: string) => {
        const response: WorkOrderItem = await workOrderService.getWorkOrderById(id);
        return response;
    }
);

// Async thunk to create work order
export const createWorkOrder = createAsyncThunk(
    "workOrders/createWorkOrder",
    async ({ payload, attachments = [] }: { payload: WorkOrderCreatePayload; attachments?: File[] }) => {
        // Use WithAttachments endpoint if attachments are provided
        if (attachments && attachments.length > 0) {
            const response: WorkOrderItem = await workOrderService.createWorkOrderWithAttachments(payload, attachments);
            return response;
        } else {
            const response: WorkOrderItem = await workOrderService.createWorkOrder(payload);
            return response;
        }
    }
);

// Async thunk to update work order
export const updateWorkOrder = createAsyncThunk(
    "workOrders/updateWorkOrder",
    async ({ id, payload, attachments = [] }: { id: string; payload: WorkOrderUpdatePayload; attachments?: File[] }) => {
        // Use WithAttachments endpoint if attachments are provided
        if (attachments && attachments.length > 0) {
            const response: WorkOrderItem = await workOrderService.updateWorkOrderWithAttachments(id, payload, attachments);
            return response;
        } else {
            const response: WorkOrderItem = await workOrderService.updateWorkOrder(id, payload);
            return response;
        }
    }
);

// Async thunk to delete work order
export const deleteWorkOrder = createAsyncThunk(
    "workOrders/deleteWorkOrder",
    async (id: string) => {
        await workOrderService.deleteWorkOrder(id);
        return id;
    }
);

const workOrderSlice = createSlice({
    name: "WorkOrders",
    initialState,
    reducers: {
        clearError(state) {
            state.error = null;
        },
        clearCurrentItem(state) {
            state.currentItem = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch work orders
            .addCase(fetchWorkOrders.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWorkOrders.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.items;
                state.pageNumber = action.payload.pageNumber;
                state.pageSize = action.payload.pageSize;
                state.totalCount = action.payload.totalCount;
                state.totalPages = action.payload.totalPages;
            })
            .addCase(fetchWorkOrders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch work orders";
            })
            // Fetch work order by ID
            .addCase(fetchWorkOrderById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWorkOrderById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentItem = action.payload;
            })
            .addCase(fetchWorkOrderById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch work order";
            })
            // Create work order
            .addCase(createWorkOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createWorkOrder.fulfilled, (state, action) => {
                state.loading = false;
                state.currentItem = action.payload;
            })
            .addCase(createWorkOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to create work order";
            })
            // Update work order
            .addCase(updateWorkOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateWorkOrder.fulfilled, (state, action) => {
                state.loading = false;
                state.currentItem = action.payload;
                // Update item in list if it exists
                const index = state.items.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.items[index] = action.payload;
                }
            })
            .addCase(updateWorkOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to update work order";
            })
            // Delete work order
            .addCase(deleteWorkOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteWorkOrder.fulfilled, (state, action) => {
                state.loading = false;
                // Remove deleted item from list
                state.items = state.items.filter((item) => item.id !== action.payload);
                // Clear current item if it was deleted
                if (state.currentItem?.id === action.payload) {
                    state.currentItem = null;
                }
            })
            .addCase(deleteWorkOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to delete work order";
            });
    },
});

export const { clearError, clearCurrentItem } = workOrderSlice.actions;
export default workOrderSlice.reducer;

// Selectors
export const selectWorkOrderList = (state: any) => state.WorkOrders.items;
export const selectWorkOrderById = (state: any, id: string) =>
    state.WorkOrders.items.find((wo: WorkOrderItem) => wo.id === id);
export const selectCurrentWorkOrder = (state: any) => state.WorkOrders.currentItem;
export const selectWorkOrderLoading = (state: any) => state.WorkOrders.loading;
export const selectWorkOrderError = (state: any) => state.WorkOrders.error;
export const selectWorkOrderTotalCount = (state: any) => state.WorkOrders.totalCount;
export const selectWorkOrderPagination = (state: any) => ({
    pageNumber: state.WorkOrders.pageNumber,
    pageSize: state.WorkOrders.pageSize,
    totalCount: state.WorkOrders.totalCount,
    totalPages: state.WorkOrders.totalPages,
});

